import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Link, Typography } from '@mui/material';
import moment from 'moment';

import {
  ClientInvitationFragment,
  DeleteClientInvitationsMutation,
  DeleteClientInvitationsMutationVariables,
} from 'api';
import { CheckedMode, InfiniteQuery, UseCheckedState } from 'types/app';

import { DataTable, DataTableActions, DataTableColumn, DataTableRowSelection } from 'components';

import { getFullName } from 'helpers';
import { generateLink } from 'Links';

import Actions from './Actions';
import { UseMutationOptions } from 'react-query';

type DeleteMutationOptions = UseMutationOptions<
  DeleteClientInvitationsMutation,
  unknown,
  DeleteClientInvitationsMutationVariables
>;

interface Props extends Partial<UseCheckedState>, InfiniteQuery<ClientInvitationFragment> {
  deleteMutationOptions: DeleteMutationOptions;
  count: number;
}

const AdminPendingInvitationsTable: FC<Props> = ({ deleteMutationOptions, ...rest }) => {
  const { t } = useTranslation();

  const { sentinelRef, results, isExhausted, isInitialLoad, count } = rest;
  const { setCheckedState, checkedMode, checkedIds, resetCheckedState } = rest;
  const isAllChecked = checkedMode !== CheckedMode.OFF;

  function handleAllCheckedChange(event: ChangeEvent<HTMLInputElement>) {
    setCheckedState(event.target.checked ? CheckedMode.PAGE : CheckedMode.OFF);
  }

  function handleCheckedChange(checkedId: string, checked: boolean) {
    let nextCheckedIds = [];

    if (checkedMode) {
      setCheckedState(CheckedMode.OFF);
      nextCheckedIds = results.map(item => item.id).filter(id => id !== checkedId);
    } else if (checked) {
      nextCheckedIds = checkedIds.slice();
      nextCheckedIds.push(checkedId);
    } else {
      nextCheckedIds = checkedIds.filter(id => id !== checkedId);
    }

    setCheckedState(nextCheckedIds);
  }

  const columns: DataTableColumn<ClientInvitationFragment>[] = [
    {
      width: 50,
      headerCell: {
        padding: 'checkbox',
        render: <Checkbox checked={isAllChecked} onChange={handleAllCheckedChange} />,
      },
      bodyCell: {
        padding: 'checkbox',
        render: invitation => (
          <Checkbox
            name={invitation.id}
            checked={isAllChecked || checkedIds.includes(invitation.id)}
            onChange={event => handleCheckedChange(invitation.id, event.target.checked)}
          />
        ),
      },
    },
    {
      width: 400,
      headerCell: {
        render: t('common:email'),
      },
      bodyCell: {
        render: invitation => (
          <Typography noWrap variant="body1" title={invitation.email_address}>
            {invitation.email_address}
          </Typography>
        ),
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('admin:role'),
      },
      bodyCell: {
        render: invitation => <Typography>{invitation.user_type}</Typography>,
      },
    },
    {
      width: 250,
      headerCell: {
        render: t('admin:invitedBy'),
      },
      bodyCell: {
        render: invitation => (
          <Link
            noWrap
            style={{ display: 'block' }}
            color="textSecondary"
            title={getFullName(invitation.added_by_user)}
            component={generateLink(`/profile/${invitation?.added_by_user?.id}`)}
            underline="hover"
          >
            {getFullName(invitation.added_by_user)}
          </Link>
        ),
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('admin:sent'),
      },
      bodyCell: {
        render: invitation => (
          <Typography title={moment.unix(invitation.created_at).format('l')}>
            {moment.unix(invitation.created_at).format('l')}
          </Typography>
        ),
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<ClientInvitationFragment> = invitation => (
    <Actions
      variables={{ invitation_ids: [invitation.id] }}
      count={1}
      resetCheckedState={resetCheckedState}
      deleteMutationOptions={deleteMutationOptions}
    />
  );

  return (
    <DataTable
      pagination={pagination}
      columns={columns}
      actions={actions}
      data={results}
      rowSelection={
        <DataTableRowSelection
          mode={checkedMode}
          noun={t('common:invite', { count })}
          count={results.length}
          total={count}
          onSelectAll={setCheckedState}
        />
      }
    />
  );
};

export default AdminPendingInvitationsTable;
